import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import CategorizedUpload from 'tio-common/components/tuition-assistance/forms/categorized-upload';
import UploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import { collectApplicationAssets } from 'tio-employee/utils/tas/application-assets';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';
import type PartnerService from 'tio-employee/services/partner';

/** The approval-step classification this card collects — the course-approval
 * (pre-approval) counterpart of submit-evidence's COMPLETION_DOCUMENTS. */
const COURSE_APPROVAL_CLASSIFICATION = 'COURSE_APPROVAL_DOCUMENTS';

/**
 * Every live COURSE_APPROVAL_DOCUMENTS asset reachable from the application —
 * course-level and application-level, via the same model getters the legacy
 * show page's submit gate reads (`tas-course.ext`'s `approvalAssets`,
 * `tas-application.ext`'s `applicationApprovalAssets`), so "has documents"
 * here can never disagree with that page. This is the classification-filtered
 * slice of show.gts's `allAttachments` union (courseAssets + appAssets;
 * program-instance assets carry program-level classifications, which the
 * filter would drop anyway). Shared with ManagePage's `attentionModel`
 * derivation so the card's list and the task's completed/pending status are
 * computed from one place.
 */
export function courseApprovalDocumentAssets(application: TasApplicationModel): TasAssetModel[] {
  const courseAssets = (application.activeCourses ?? []).flatMap(
    (course) => course.approvalAssets ?? []
  );
  const applicationAssets = application.applicationApprovalAssets ?? [];
  // applicationApprovalAssets also admits APPLICATION_LIFECYCLE_DOCUMENTS (it
  // serves broader surfaces), so the union is narrowed to the one
  // classification this card collects — an unrelated lifecycle document must
  // not complete the task or appear in its list. (The course-level getter is
  // already classification-pure; filtering the union keeps that invariant in
  // one place.)
  return [...courseAssets, ...applicationAssets].filter(
    (asset) => asset.classification === COURSE_APPROVAL_CLASSIFICATION
  );
}

interface CourseDocumentsBodySignature {
  Args: {
    application: TasApplicationModel;
  };
  Element: HTMLDivElement;
}

/**
 * Collapsible body of the "Upload your Approval Documents" task (#10292): the
 * course-approval-documents surface the legacy show page renders during its
 * `courseApprovalStates` window and the manage screen previously lacked
 * entirely. Required/optional wording, the list of already-uploaded
 * course-approval documents, and the same CategorizedUpload/UploadAttachments
 * upload machinery submit-evidence uses — scoped to COURSE_APPROVAL_DOCUMENTS
 * instead of COMPLETION_DOCUMENTS. Uploads persist through those components'
 * own digital-assets flow (identical to the legacy page and submit-evidence);
 * nothing here needs a ManagePage-owned bundle.
 */
export default class CourseDocumentsBody extends Component<CourseDocumentsBodySignature> {
  @service declare partner: PartnerService;

  get application() {
    return this.args.application;
  }

  get programTemplate() {
    return this.application.tasProgramInstance?.tasProgramTemplate;
  }

  /** The template's required flag, via the model getter the legacy page's
   * asterisk keys off (`isPreApprovalAttachmentsRequired` →
   * `requireAttachmentsForCourseApproval`) — drives the wording only; the
   * surface renders either way once visible. */
  get isRequired(): boolean {
    return Boolean(this.application.isPreApprovalAttachmentsRequired);
  }

  // Company capability, not a per-browser beta opt-in — same rule as the
  // legacy show page and submit-evidence.gts (#9889): AI summaries not
  // disabled for the company, and the template configures document categories
  // for THIS classification.
  get useCategorizedUpload(): boolean {
    if (this.partner.companySettings?.tuitionAssistance?.isAiSummariesEnabled === false) {
      return false;
    }
    const entries = this.programTemplate?.documentEntriesFor(COURSE_APPROVAL_CLASSIFICATION) ?? [];
    return entries.length > 0;
  }

  /** Already-uploaded course-approval documents — the classification-filtered
   * equivalent of the legacy page's `allAttachments` list (that page lists
   * every classification with a label chip per row; this card is scoped to
   * one step, so it lists only that step's documents). */
  get uploadedDocuments(): TasAssetModel[] {
    return courseApprovalDocumentAssets(this.application);
  }

  /**
   * The list the card RENDERS: every course-approval document attached at either
   * level, including soft-deleted ones, deduplicated.
   *
   * Reads `tasAssets` directly rather than going through `approvalAssets` /
   * `applicationApprovalAssets`, because both of those bake a liveness filter
   * into the model — which is why a removed file could not appear here no matter
   * what `@hideDiscarded` said.
   *
   * Deliberately NOT `courseApprovalDocumentAssets`: that one decides the task's
   * completed/pending status (via ManagePage's `hasCourseApprovalDocuments`), and
   * a deleted document must never complete a task. Same split the categorized
   * uploader keeps between what it shows and what it gates on.
   */
  get documentsForDisplay(): TasAssetModel[] {
    const courseAssets = (this.application.activeCourses ?? []).flatMap(
      (course) => course.tasAssets ?? []
    );
    // An asset attached at both levels would otherwise render twice.
    const seen = new Set<string>();

    return [...courseAssets, ...(this.application.tasAssets ?? [])].filter((asset) => {
      if (asset.classification !== COURSE_APPROVAL_CLASSIFICATION) {
        return false;
      }

      const key = asset.id ?? asset.uuid;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  /** Everything attached at any level — CategorizedUpload's `@relatedAssets`,
   * which feeds its view-only "Previously uploaded" history (documents from
   * other steps; its own dedup drops whatever the rows above already show).
   * The legacy page passes its allAttachments union the same way (#10393). */
  get allRelatedAssets(): TasAssetModel[] {
    return collectApplicationAssets(this.application);
  }

  <template>
    <div class="tio-col gap-4" data-test-course-documents-body ...attributes>
      <p class="tio-text-sm-normal tio-fg-secondary" data-test-course-documents-note>
        {{#if this.isRequired}}
          {{t "tuition_assistance.applications.manage.tasks.course_documents_note_required"}}
        {{else}}
          {{t "tuition_assistance.applications.manage.tasks.course_documents_note_optional"}}
        {{/if}}
      </p>

      <div class="tio-col gap-2" data-test-course-documents-upload>
        <p class="tio-text-sm-semibold tio-fg-primary">
          {{t "tuition_assistance.applications.manage.tasks.course_documents_upload_label"}}
        </p>
        {{#if this.useCategorizedUpload}}
          {{! Both branches render the SAME classification-filtered union the
            task's completed/pending status is computed from — passing raw
            application.tasAssets here would hide a course-owned approval
            document that already marked the task completed. CategorizedUpload's
            own classification filter is a no-op over this pre-filtered list. }}
          <CategorizedUpload
            @programTemplate={{this.programTemplate}}
            @classification={{COURSE_APPROVAL_CLASSIFICATION}}
            @attachable={{this.application}}
            @attachableType="tasApplication"
            @existingAssets={{this.uploadedDocuments}}
            @relatedAssets={{this.allRelatedAssets}}
          />
        {{else}}
          {{! The free-form list renders exactly what it is given, so it gets the
            display union — which carries removed files, unlike the one the task's
            status is derived from. }}
          <UploadAttachments
            @programTemplate={{this.programTemplate}}
            @classification={{COURSE_APPROVAL_CLASSIFICATION}}
            @attachable={{this.application}}
            @attachableType="tasApplication"
            @useDropzone={{true}}
            @attachmentListItems={{this.documentsForDisplay}}
            @hideDiscarded={{false}}
          />
        {{/if}}
      </div>
    </div>
  </template>
}
